// client/src/coms/components/EditChannelModal.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ChannelPermissionsEditor from './ChannelPermissionsEditor';
import ComsModal from './ComsModal';

const DEFAULT_WELCOME_MESSAGE = 'Welcome {user} to {server}!';

async function apiFetchJson(url, opts) {
  const res = await fetch(url, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts && opts.headers ? opts.headers : {}),
    },
  });

  let data = null;
  const ct = String(res.headers.get('content-type') || '');
  if (ct.includes('application/json')) {
    try {
      data = await res.json();
    } catch {}
  } else {
    try {
      const t = await res.text();
      data = t ? { text: t } : null;
    } catch {}
  }

  if (!res.ok) {
    const msg =
      (data && (data.error || data.detail || data.message)) ||
      `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function roleLabel(role) {
  const name = String((role && role.name) || '').trim();
  return name || 'Role';
}

function normalizeRoleIds(value) {
  return Array.from(
    new Set(
      safeArr(value)
        .map((x) => toInt(x, 0))
        .filter((x) => x > 0)
    )
  );
}

function channelDbId(channel) {
  if (!channel) return '';
  if (channel.db_id != null) return String(channel.db_id).trim();
  if (channel.id != null) return String(channel.id).trim();
  return '';
}

function capitalise(str) {
  const s = String(str || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getMapLabel(map) {
  const name = String((map && map.name) || '').trim();
  const gameName = String((map && map.game_name) || '').trim();

  if (!name && !gameName) return `Map ${toInt(map && map.id, 0)}`;
  if (!name) return capitalise(gameName);
  if (!gameName) return name;
  if (name.toLowerCase() === gameName.toLowerCase()) return name;
  return `${name} — ${capitalise(gameName)}`;
}

function normalizeWelcomeTemplate(value) {
  const text = String(value == null ? '' : value).trim();
  return text ? text.slice(0, 2000) : '';
}

function normalizeFetchedChannel(found, fallbackChannel) {
  const base =
    found && typeof found === 'object'
      ? found
      : fallbackChannel && typeof fallbackChannel === 'object'
        ? fallbackChannel
        : {};

  return {
    ...base,
    kind: String((base && base.kind) || '').trim().toLowerCase(),
    name: String((base && base.name) || '').trim(),
    topic: String((base && base.topic) || ''),
    visible_role_ids: normalizeRoleIds(base && base.visible_role_ids),
    // Missing means public (older payloads); only an explicit 0 is a private channel.
    visible_to_all: base && base.visible_to_all != null ? (Number(base.visible_to_all) === 0 ? 0 : 1) : 1,
    linked_map_id: base && base.linked_map_id != null ? toInt(base.linked_map_id, 0) || null : null,
  };
}

function rowStyle(isCompact) {
  return {
    display: 'grid',
    gridTemplateColumns: isCompact ? '1fr' : '120px minmax(0, 1fr)',
    gap: isCompact ? 8 : 12,
    alignItems: 'start',
  };
}

function labelStyle(isCompact) {
  return {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
    paddingTop: isCompact ? 0 : 9,
  };
}

function fieldBoxStyle() {
  return {
    minWidth: 0,
  };
}

function inputStyle() {
  return {
    width: '100%',
    minHeight: 34,
    borderRadius: 10,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
    color: 'var(--vx-text, #fff)',
    padding: '0 12px',
    boxSizing: 'border-box',
    outline: 'none',
  };
}

function textAreaStyle() {
  return {
    width: '100%',
    minHeight: 96,
    borderRadius: 10,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
    color: 'var(--vx-text, #fff)',
    padding: '10px 12px',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    font: 'inherit',
  };
}

function panelStyle() {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '10px 12px',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    borderRadius: 10,
    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
    width: '100%',
    boxSizing: 'border-box',
  };
}

function dropdownButtonStyle(open, disabled) {
  return {
    width: '100%',
    minHeight: 36,
    borderRadius: 10,
    border: open ? '1px solid rgba(var(--vx-link-rgb, 100,140,255), 0.65)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: disabled ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1)))' : 'var(--vxs-2, #0f1724)',
    color: 'var(--vx-text, #fff)',
    padding: '0 12px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    cursor: disabled ? 'default' : 'pointer',
    boxShadow: open ? '0 0 0 2px rgba(var(--vx-link-rgb, 100,140,255), 0.12)' : 'none',
  };
}

function dropdownMenuStyle() {
  return {
    marginTop: 8,
    borderRadius: 10,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: 'var(--vxs-2, #0f1724)',
    overflow: 'hidden',
    boxShadow: '0 18px 36px rgba(0,0,0,0.35)',
  };
}

function dropdownItemStyle(selected) {
  return {
    width: '100%',
    minHeight: 36,
    padding: '0 12px',
    border: 0,
    background: selected ? 'rgba(110,168,255,0.18)' : 'transparent',
    color: 'var(--vx-text, #fff)',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  };
}

export default function EditChannelModal({ server, channel, user, onClose, onSaved }) {
  const initialChannel = useMemo(() => normalizeFetchedChannel(channel, channel), [channel]);

  const [name, setName] = useState(() => String(initialChannel.name || ''));
  const [topic, setTopic] = useState(() => String(initialChannel.topic || ''));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const [rolesBusy, setRolesBusy] = useState(false);
  const [rolesErr, setRolesErr] = useState('');
  const [roles, setRoles] = useState([]);

  const [channelBusy, setChannelBusy] = useState(false);
  const [channelErr, setChannelErr] = useState('');

  const [mapsBusy, setMapsBusy] = useState(false);
  const [mapsErr, setMapsErr] = useState('');
  const [maps, setMaps] = useState([]);

  // Whether the channel is currently visible to everyone: saving with no roles selected keeps it that way
  // (it used to always send visible_to_all 0, which made a public channel owner-only on any edit).
  const [wasVisibleToAll, setWasVisibleToAll] = useState(() => (initialChannel.visible_to_all != null && Number(initialChannel.visible_to_all) === 0 ? 0 : 1));
  const [selectedRoleIds, setSelectedRoleIds] = useState(() =>
    normalizeRoleIds(initialChannel.visible_role_ids)
  );
  const [linkedMapId, setLinkedMapId] = useState(() =>
    initialChannel.linked_map_id != null ? String(initialChannel.linked_map_id) : ''
  );
  const [mapMenuOpen, setMapMenuOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [useAsWelcome, setUseAsWelcome] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(() => {
    const existing = normalizeWelcomeTemplate(server && server.welcome_message_template);
    return existing || DEFAULT_WELCOME_MESSAGE;
  });

  const mapMenuRef = useRef(null);

  const serverId = server && server.id != null ? String(server.id) : '';
  const currentChannelDbId = channelDbId(channel);
  const canSubmit = String(name || '').trim().length >= 1;
  const channelKind = String((initialChannel && initialChannel.kind) || '').trim().toLowerCase();
  const isVoice = channelKind === 'voice';
  const isText = channelKind === 'text';
  const userId = user && user.id != null ? toInt(user.id, 0) : 0;
  const isFounder = userId === 1;

  useEffect(() => {
    function updateCompact() {
      if (typeof window === 'undefined') {
        setIsCompact(false);
        return;
      }
      setIsCompact(window.innerWidth <= 640);
    }

    updateCompact();
    window.addEventListener('resize', updateCompact);
    return () => window.removeEventListener('resize', updateCompact);
  }, []);

  useEffect(() => {
    const normalized = normalizeFetchedChannel(channel, channel);
    setName(String(normalized.name || ''));
    setTopic(String(normalized.topic || ''));
    setSelectedRoleIds(normalizeRoleIds(normalized.visible_role_ids));
    setWasVisibleToAll(normalized.visible_to_all);
    setLinkedMapId(normalized.linked_map_id != null ? String(normalized.linked_map_id) : '');
    setMapMenuOpen(false);
  }, [channel]);

  useEffect(() => {
    const existing = normalizeWelcomeTemplate(server && server.welcome_message_template);
    setWelcomeMessage(existing || DEFAULT_WELCOME_MESSAGE);
  }, [server]);

  useEffect(() => {
    const welcomeId = toInt(server && server.welcome_channel_id, 0);
    const channelId = toInt(currentChannelDbId, 0);
    setUseAsWelcome(isText && welcomeId > 0 && channelId > 0 && welcomeId === channelId);
  }, [server, currentChannelDbId, isText]);

  useEffect(() => {
    let alive = true;

    async function loadRoles() {
      if (!serverId) {
        if (alive) {
          setRoles([]);
          setRolesErr('');
          setRolesBusy(false);
        }
        return;
      }

      setRolesBusy(true);
      setRolesErr('');

      try {
        const data = await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverId)}/roles`, {
          method: 'GET',
        });

        if (!alive) return;

        const nextRoles = safeArr(data && data.roles)
          .filter((role) => toInt(role && role.is_owner, 0) !== 1)
          .map((role) => ({
            ...role,
            id: toInt(role && role.id, 0),
          }))
          .filter((role) => role.id > 0);

        setRoles(nextRoles);
      } catch (e) {
        if (!alive) return;
        setRoles([]);
        setRolesErr(e?.message || 'Failed to load roles');
      } finally {
        if (alive) setRolesBusy(false);
      }
    }

    loadRoles().catch(() => {});

    return () => {
      alive = false;
    };
  }, [serverId]);

  useEffect(() => {
    let alive = true;

    async function loadMaps() {
      if (!isVoice) {
        if (alive) {
          setMaps([]);
          setMapsBusy(false);
          setMapsErr('');
          setMapMenuOpen(false);
        }
        return;
      }

      if (!userId) {
        if (alive) {
          setMaps([]);
          setMapsBusy(false);
          setMapsErr('');
        }
        return;
      }

      setMapsBusy(true);
      setMapsErr('');

      try {
        const url = isFounder ? '/api/founder/maps' : `/api/user/id/${encodeURIComponent(userId)}/maps`;
        const data = await apiFetchJson(url, { method: 'GET' });

        if (!alive) return;

        const nextMaps = safeArr(data)
          .map((map) => ({
            ...map,
            id: toInt(map && map.id, 0),
            parent_map_id: map && map.parent_map_id != null ? toInt(map.parent_map_id, 0) : null,
          }))
          .filter((map) => map.id > 0)
          .filter((map) => !map.parent_map_id);

        setMaps(nextMaps);
      } catch (e) {
        if (!alive) return;
        setMaps([]);
        setMapsErr(e?.message || 'Failed to load maps');
      } finally {
        if (alive) setMapsBusy(false);
      }
    }

    loadMaps().catch(() => {});

    return () => {
      alive = false;
    };
  }, [userId, isFounder, isVoice]);

  useEffect(() => {
    let alive = true;

    async function loadCurrentChannel() {
      if (!serverId || !currentChannelDbId) {
        if (alive) {
          setChannelBusy(false);
          setChannelErr('');
        }
        return;
      }

      setChannelBusy(true);
      setChannelErr('');

      try {
        const data = await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverId)}/channels`, {
          method: 'GET',
        });

        if (!alive) return;

        const text = safeArr(data && data.text_channels);
        const voice = safeArr(data && data.voice_channels);
        const allChannels = [...text, ...voice];

        const found = allChannels.find((c) => String(c && c.id != null ? c.id : '') === String(currentChannelDbId));
        const normalized = normalizeFetchedChannel(found, channel);

        setName(normalized.name);
        setTopic(normalized.topic);
        setSelectedRoleIds(normalized.visible_role_ids);
        setWasVisibleToAll(normalized.visible_to_all);
        setLinkedMapId(normalized.linked_map_id != null ? String(normalized.linked_map_id) : '');
      } catch (e) {
        if (!alive) return;
        setChannelErr(e?.message || 'Failed to load current channel');
      } finally {
        if (alive) setChannelBusy(false);
      }
    }

    loadCurrentChannel().catch(() => {});

    return () => {
      alive = false;
    };
  }, [serverId, currentChannelDbId, channel]);

  useEffect(() => {
    if (!mapMenuOpen) return;

    function onDown(e) {
      const el = mapMenuRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setMapMenuOpen(false);
    }

    function onKey(e) {
      if (e && e.key === 'Escape') setMapMenuOpen(false);
    }

    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);

    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [mapMenuOpen]);

  const selectedRoleIdSet = useMemo(() => {
    return new Set(normalizeRoleIds(selectedRoleIds));
  }, [selectedRoleIds]);

  const selectedMap = useMemo(() => {
    const id = toInt(linkedMapId, 0);
    if (!id) return null;
    return safeArr(maps).find((map) => toInt(map && map.id, 0) === id) || null;
  }, [linkedMapId, maps]);

  function toggleRole(roleId) {
    const id = toInt(roleId, 0);
    if (!id) return;

    setSelectedRoleIds((prev) => {
      const list = normalizeRoleIds(prev);
      if (list.includes(id)) return list.filter((x) => x !== id);
      return [...list, id];
    });
  }

  function chooseLinkedMap(id) {
    const next = toInt(id, 0);
    setLinkedMapId(next > 0 ? String(next) : '');
    setMapMenuOpen(false);
  }

  async function submit(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!canSubmit || busy) return;

    setBusy(true);
    setErr('');

    try {
      if (!serverId) throw new Error('Missing server id');
      if (!currentChannelDbId) throw new Error('Missing channel id');

      const roleIds = normalizeRoleIds(selectedRoleIds);

      const payload = {
        name: String(name || '').trim(),
        // Roles selected: private to them. None: unchanged (public stays public, owner-only stays owner-only).
        visible_to_all: roleIds.length ? 0 : wasVisibleToAll,
        role_ids: roleIds,
      };

      if (isVoice) {
        payload.linked_map_id = linkedMapId ? toInt(linkedMapId, 0) || null : null;
      } else {
        payload.topic = String(topic || '').trim();
      }

      const data = await apiFetchJson(
        `/api/coms/servers/${encodeURIComponent(serverId)}/channels/${encodeURIComponent(currentChannelDbId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      );
      window.dispatchEvent(new CustomEvent('vox-channels-changed'));

      if (isText) {
        const currentWelcomeChannelId = toInt(server && server.welcome_channel_id, 0);
        const thisChannelId = toInt(currentChannelDbId, 0);
        const nextWelcomeTemplate = normalizeWelcomeTemplate(welcomeMessage) || DEFAULT_WELCOME_MESSAGE;

        if (useAsWelcome && thisChannelId > 0) {
          await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverId)}`, {
            method: 'PATCH',
            body: JSON.stringify({
              welcome_channel_id: thisChannelId,
              welcome_message_enabled: 1,
              welcome_message_template: nextWelcomeTemplate,
            }),
          });
        } else if (!useAsWelcome && thisChannelId > 0 && currentWelcomeChannelId === thisChannelId) {
          await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverId)}`, {
            method: 'PATCH',
            body: JSON.stringify({
              welcome_channel_id: null,
              welcome_message_enabled: 0,
            }),
          });
        }
      }

      const savedChannel = normalizeFetchedChannel(data && data.channel, channel);

      setName(savedChannel.name);
      if (!isVoice) setTopic(savedChannel.topic);
      setSelectedRoleIds(savedChannel.visible_role_ids);
      setWasVisibleToAll(savedChannel.visible_to_all);
      setLinkedMapId(savedChannel.linked_map_id != null ? String(savedChannel.linked_map_id) : '');

      if (typeof onSaved === 'function') {
        try {
          await onSaved({
            channel: savedChannel,
            raw: data,
          });
        } catch {}
      }

      onClose();
    } catch (e2) {
      setErr(e2?.message || 'Failed to update channel');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ComsModal
      title={`Edit ${isVoice ? 'voice' : 'text'} channel`}
      onClose={onClose}
      width={isCompact ? 'calc(100vw - 20px)' : '460px'}
      maxWidth="calc(100vw - 20px)"
      footer={
        <>
          <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="coms-mini-btn primary" onClick={submit} disabled={!canSubmit || busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form
        onSubmit={submit}
        className="coms-form"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          width: '100%',
          minWidth: 0,
        }}
      >
        <div style={rowStyle(isCompact)}>
          <div style={labelStyle(isCompact)}>Type</div>

          <div style={fieldBoxStyle()}>
            <div style={{ minHeight: 34, display: 'flex', alignItems: 'center', opacity: 0.9 }}>
              {isVoice ? '🔊 Voice' : '# Text'}
            </div>
          </div>
        </div>

        <div style={rowStyle(isCompact)}>
          <div style={labelStyle(isCompact)}>Name</div>

          <div style={fieldBoxStyle()}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isVoice ? 'Voice Lobby' : 'general'}
              autoFocus
              style={inputStyle()}
            />
          </div>
        </div>

        {isText ? (
          <div style={rowStyle(isCompact)}>
            <div style={labelStyle(isCompact)}>Topic</div>

            <div style={fieldBoxStyle()}>
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="What this channel is for (shown in the chat header)"
                maxLength={1024}
                aria-label="Channel topic"
                data-testid="edit-channel-topic"
                style={{ ...textAreaStyle(), minHeight: 64 }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, opacity: 0.72 }}>
                <span>Optional. Leave empty for no topic.</span>
                <span>{String(topic || '').length}/1024</span>
              </div>
            </div>
          </div>
        ) : null}

        {isText ? (
          <div style={rowStyle(isCompact)}>
            <div style={labelStyle(isCompact)}>Welcome</div>

            <div style={fieldBoxStyle()}>
              <div style={panelStyle()}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))' }}>
                      Use this as the welcome channel
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                      New member join messages will be posted in this text channel.
                    </div>
                  </span>

                  <input
                    type="checkbox"
                    checked={!!useAsWelcome}
                    onChange={(e) => setUseAsWelcome(!!e.target.checked)}
                    style={{ flexShrink: 0 }}
                  />
                </label>

                {useAsWelcome ? (
                  <>
                    <div style={{ fontSize: 12, opacity: 0.82, marginTop: 4 }}>
                      You can use {'{user}'}, {'{server}'}, and {'{memberCount}'}.
                    </div>

                    <textarea
                      value={welcomeMessage}
                      onChange={(e) => setWelcomeMessage(e.target.value)}
                      placeholder={DEFAULT_WELCOME_MESSAGE}
                      maxLength={2000}
                      style={textAreaStyle()}
                    />

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, opacity: 0.72 }}>
                      <span>Leave blank to use the default welcome message.</span>
                      <span>{String(welcomeMessage || '').length}/2000</span>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {isVoice ? (
          <div style={rowStyle(isCompact)}>
            <div style={labelStyle(isCompact)}>Linked map</div>

            <div style={fieldBoxStyle()}>
              <div ref={mapMenuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => {
                    if (mapsBusy) return;
                    setMapMenuOpen((v) => !v);
                  }}
                  disabled={mapsBusy}
                  style={dropdownButtonStyle(mapMenuOpen, mapsBusy)}
                >
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                    }}
                  >
                    {selectedMap ? getMapLabel(selectedMap) : 'No linked map'}
                  </span>
                  <span style={{ flexShrink: 0, opacity: 0.8 }}>{mapMenuOpen ? '▴' : '▾'}</span>
                </button>

                {mapMenuOpen ? (
                  <div style={dropdownMenuStyle()}>
                    <button
                      type="button"
                      onClick={() => chooseLinkedMap('')}
                      style={dropdownItemStyle(!linkedMapId)}
                    >
                      No linked map
                    </button>

                    {maps.map((map) => {
                      const selected = String(linkedMapId) === String(map.id);
                      return (
                        <button
                          key={map.id}
                          type="button"
                          onClick={() => chooseLinkedMap(map.id)}
                          style={dropdownItemStyle(selected)}
                        >
                          {getMapLabel(map)}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                Pick one of your maps to link this voice channel to.
              </div>

              {mapsBusy ? <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8 }}>Loading maps…</div> : null}

              {!mapsBusy && mapsErr ? (
                <div className="coms-form-error" style={{ marginTop: 8 }}>
                  {mapsErr}
                </div>
              ) : null}

              {!mapsBusy && !mapsErr && maps.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8 }}>No online maps found.</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div style={rowStyle(isCompact)}>
          <div style={labelStyle(isCompact)}>Visible to roles</div>

          <div style={fieldBoxStyle()}>
            <div
              style={{
                ...panelStyle(),
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Owner always has access and is not shown here.{' '}
                {selectedRoleIds.length ? 'Only the selected roles can see this channel.' : wasVisibleToAll ? 'No roles selected: everyone in the server can see it.' : 'No roles selected: only the owner can see it.'}
              </div>

              {rolesBusy || channelBusy ? <div style={{ fontSize: 13, opacity: 0.8 }}>Loading…</div> : null}

              {!rolesBusy && rolesErr ? (
                <div className="coms-form-error" style={{ marginTop: 0 }}>
                  {rolesErr}
                </div>
              ) : null}

              {!channelBusy && channelErr ? (
                <div className="coms-form-error" style={{ marginTop: 0 }}>
                  {channelErr}
                </div>
              ) : null}

              {!rolesBusy && !rolesErr && roles.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.8 }}>No assignable roles found.</div>
              ) : null}

              {!rolesBusy && !rolesErr && roles.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    width: '100%',
                  }}
                >
                  {roles.map((role) => {
                    const checked = selectedRoleIdSet.has(role.id);
                    return (
                      <label
                        key={role.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          width: '100%',
                          cursor: 'pointer',
                          padding: '2px 0',
                          boxSizing: 'border-box',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0,
                          }}
                        >
                          {role.color ? (
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: String(role.color),
                                display: 'inline-block',
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                          <span
                            style={{
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {roleLabel(role)}
                          </span>
                        </span>

                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(role.id)}
                          style={{ flexShrink: 0 }}
                        />
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {toInt(initialChannel.db_id != null ? initialChannel.db_id : initialChannel.id, 0) > 0 ? (
          <div style={rowStyle(isCompact)}>
            <div style={labelStyle(isCompact)}>Permissions</div>
            <div style={fieldBoxStyle()}>
              <div style={panelStyle()}>
                <ChannelPermissionsEditor
                  kind={initialChannel.kind === 'voice' ? 'voice' : 'text'}
                  channelId={toInt(initialChannel.db_id != null ? initialChannel.db_id : initialChannel.id, 0)}
                />
              </div>
            </div>
          </div>
        ) : null}

        {err ? <div className="coms-form-error">{err}</div> : null}
      </form>
    </ComsModal>
  );
}